<template>
	<div class="modal-popup">
		<div class="modal-close" @click="onClose">
			&times;
		</div>
		<div class="modal-content">
			<h2 class="text-truncate">
				{{ details.title }}
			</h2>
			<div class="mt-25 mb-35">
				<p>{{ details.description }}</p>
			</div>
			<div class="form-group gap-40 text-center">
				<a
					:href="consultationLink"
					:title="$tc('modals.manageSubscription.title')"
					class="text-truncate button-pink mb-20 font-uppercase"
					target="_blank"
					@click="trackManageSubscriptionClick"
				>
					{{ $tc('modals.manageSubscription.title') }}
				</a>
				<template v-if="options.isClub">
					<button
						:title="details.confirmText"
						class="text-truncate button-default mb-20"
						@click="confirmAction"
					>
						{{ details.confirmText }}
					</button>
				</template>
				<template v-else>
					<button
						:title="details.cancelText"
						class="text-truncate button-default mb-20"
						@click="onClose"
					>
						{{ details.cancelText }}
					</button>
					<button
						:title="details.confirmText"
						class="text-truncate button-light-blue mb-20"
						@click="confirmAction"
					>
						{{ details.confirmText }}
					</button>
				</template>
			</div>
			<div v-if="details.confirmTextDetails" class="form-group gap-40 text-right">
				<div />
				<div />
				<div>{{ details.confirmTextDetails }}</div>
			</div>
		</div>

		<div v-if="options.isClub && options.type !== 'cancel'" class="mt-50 mb-35 text-center">
			<a class="h4 text-white font-regular" @click.prevent="cancelClubAction">{{ details.cancelText }}</a>
		</div>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { ModalMixin } from '~/assets/vue/mixins'
import { openChatbot } from '~/assets/util/helpers'
import { cameraTypesMap } from '~/assets/util/helpers/mappers'
import analytics from '~/assets/util/analytics'

const modalActions = ['title', 'description', 'cancelText', 'confirmText', 'confirmTextDetails']

export default {
	name: 'ModalManageSubscription',
	mixins: [ModalMixin],
	props: {
		options: {
			type: Object,
			required: true
		}
	},
	computed: {
		...mapGetters({
			user: 'user/user',
			consultationLink: 'user/consultationLink'
		}),

		details () {
			const subscriptionDetails = {}
			const clubParams = {
				subscriptionType: this.viseVersaSubscriptionType.toUpperCase(),
				cameraType: this.cameraType.toUpperCase(),
				cameraSN: this.cameraSN
			}

			modalActions.forEach((action) => {
				if (this.options.isClub) {
					subscriptionDetails[`${action}`] =
							this.$te(`modals.manageSubscription.clubSubscriptionsDetails.${this.actionType}.${action}`) ? this.$t(`modals.manageSubscription.clubSubscriptionsDetails.${this.actionType}.${action}`, clubParams) : ''
				} else {
					subscriptionDetails[`${action}`] =
							this.$te(`modals.manageSubscription.teamSubscriptionsDetails.${this.actionType}.${action}`) ? this.$t(`modals.manageSubscription.teamSubscriptionsDetails.${this.actionType}.${action}`) : ''
				}
			})

			return subscriptionDetails
		},

		actionType () {
			return this.options.type === 'switch'
				? this.viseVersaSubscriptionType === this.$tc('common.subscription-interval-12') ? 'upgrade' : 'downgrade'
				: this.options.type
		},

		subscriptionType () {
			if (!this.options.subscription) {
				return ''
			}

			const interval = this.options.subscription.intervalCount || this.options.subscription.plan.interval_count

			return this.$tc(`common.subscription-interval-${interval}`)
		},

		viseVersaSubscriptionType () {
			switch (this.subscriptionType) {
					case this.$tc('common.subscription-interval-1'):
						return this.$tc('common.subscription-interval-12')
					case this.$tc('common.subscription-interval-6'):
						return this.$tc('common.subscription-interval-12')
					case this.$tc('common.subscription-interval-12'):
						return this.$tc('common.subscription-interval-1')
					default:
						return ''
			}
		},

		cameraType () {
			const type = this.options.camera && this.options.camera.type

			return cameraTypesMap[type] || ''
		},

		cameraSN () {
			const serialNumber = (this.options.camera && this.options.camera.serialNumber)

			return serialNumber ? `SN: ${serialNumber}` : ''
		},

		subscriptionTitle () {
			const title = this.subscriptionType
			const isClub = this.options.isClub || (this.options.subscription && this.options.subscription.metadata.type === 'club')

			return title && title.toLowerCase() + `${isClub ? ' club level' : ''}`
		},

		actionText () {
			const params = {
				cameraType: this.cameraType,
				cameraSN: this.cameraSN,
				viseVersaSubscriptionType: this.viseVersaSubscriptionType,
				subscriptionTitle: this.subscriptionTitle
			}
			switch (this.actionType) {
					case 'upgrade':
						return this.options.isClub
							? this.$tc('modals.manageSubscription.actionTexts.upgrade', 1, params)
							: this.$tc('modals.manageSubscription.actionTexts.upgrade', 2, params)
					case 'downgrade':
						return this.options.isClub
							? this.$tc('modals.manageSubscription.actionTexts.downgrade', 1, params)
							: this.$tc('modals.manageSubscription.actionTexts.downgrade', 2, params)
					case 'reSubscribe':
						return this.options.isClub
							? this.$tc('modals.manageSubscription.actionTexts.reSubscribe', 1, params)
							: this.$tc('modals.manageSubscription.actionTexts.reSubscribe', 2, params)
					case 'cancel':
						return this.options.isClub
							? this.$tc('modals.manageSubscription.actionTexts.cancel', 1, params)
							: this.$tc('modals.manageSubscription.actionTexts.cancel', 2, params)
					default:
						return ''
			}
		}
	},
	methods: {
		...mapActions({ analyticsTrack: 'analytics/track' }),
		trackManageSubscriptionClick () {
			this.analyticsTrack({
				event: analytics.ChurnPreventionTriggerClicked.event,
				data: analytics.ChurnPreventionTriggerClicked.getData({
					trigger: 'MANAGE SUBSCRIPTION',
					source: 'manage_subscription_modal',
					subscription: this.options.subscription
				})
			})
		},
		confirmAction () {
			if (this.actionText) {
				openChatbot(this.actionText, this.user)
			}

			this.$emit('close')
		},
		cancelClubAction () {
			if (this.actionText) {
				openChatbot(this.$tc('modals.manageSubscription.actionTexts.chatBotCancel'), this.user)
			}

			this.$emit('close')
		}
	}
}
</script>
